import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BusinessOutlined,
  Close,
  Refresh,
  WarningAmber,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  useCreateClientMutation,
  useUpdateClientMutation,
  useArchiveClientMutation,
  useUpdateAdminUserMutation,
  useGetAdminUsersQuery,
  useLazyCheckClientCodeAvailabilityQuery,
  useLazyCheckClientNameAvailabilityQuery,
} from "features/adminUsers/adminUsers";
import { useAdminUsersAutocompleteInfinite } from "features/adminUsers/useAdminUsersAutocompleteInfinite";
import { useGetServiceTeamsQuery } from "features/accessGroups/accessGroupsApi";
import {
  platformUserAutocompleteComponentsProps,
  PlatformUserAutocompleteListbox,
  PlatformUserLabelBlock,
  renderPlatformUserAutocompleteOption,
  formatPlatformUserOptionLabel,
} from "components/common/PlatformUserLabelBlock";
import { useAppDispatch, useAppSelector } from "features/store";
import { pushNotification } from "features/notificationSlice";
import { Client, ClientOwner, PlatformUser } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import ConfirmChangesDialog, {
  accessChangeSection,
} from "../common/ConfirmChangesDialog";
import type { ConfirmAccessChangesSection } from "./UserManagementPanel";
import { usePermissions } from "hooks/usePermissions";
import { ACTIONS } from "utils/access";
import FieldLabel from "components/common/FieldLabel";
import FormFooter, { FOOTER_BUTTON_SX } from "./form/FormFooter";
import FormHeader, { StatusChip } from "./form/FormHeader";
import StepHeading from "./form/StepHeading";
import StepTabs, { type FormStep } from "./form/StepTabs";
import { nextOpenStep } from "./form/stepFlow";
import SecureExchangeRoleLabel from "./SecureExchangeRoleLabel";
import {
  hiddenReason,
  hiddenText,
  ruleOfOwner,
  teamMissing,
  type ClientMarks,
} from "./clientOwnershipModel";

type OwnerRef = ClientOwner;

interface ClientEditModalProps {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onSaved: (client: Client) => void;
  onUnsavedChange?: (dirty: boolean) => void;
  onUnsavedSummaryChange?: (summary: string[]) => void;
}

type TabId = "info" | "ownership" | "users";

function generateCode(): string {
  const digits = "23456789";
  const letters = "ABCDEFGHJKMNPQRSTUVWXYZ";
  const d = Array.from({ length: 3 }, () =>
    digits.charAt(Math.floor(Math.random() * digits.length)),
  ).join("");
  const l = Array.from({ length: 3 }, () =>
    letters.charAt(Math.floor(Math.random() * letters.length)),
  ).join("");
  return d + l;
}

function formatSince(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ownerName(owner: OwnerRef): string {
  return [owner.first_name, owner.last_name].filter(Boolean).join(" ").trim();
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function lowerFirst(text: string): string {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

function sameIds(a: number[], b: number[]): boolean {
  return JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
}

export default function ClientEditModal({
  open,
  client,
  onClose,
  onSaved,
  onUnsavedChange,
  onUnsavedSummaryChange,
}: ClientEditModalProps) {
  const isCreate = client === null;
  const dispatch = useAppDispatch();
  const me = useAppSelector((state) => state.auth.user);
  // Whoever creates a client starts among its owners.
  const creatorRef = useRef<OwnerRef | null>(null);
  const meGroup = me?.platform_profile?.access?.group;
  creatorRef.current =
    me && me.platform_profile?.user_type === "internal"
      ? {
          id: me.id,
          username: me.username,
          first_name: me.first_name,
          last_name: me.last_name ?? "",
          access_group: meGroup
            ? {
                slug: meGroup.slug,
                name: meGroup.name,
                team: meGroup.team ?? null,
                client_scope: meGroup.client_scope ?? "assigned",
                demo_clients: meGroup.demo_clients ?? "include",
              }
            : null,
        }
      : null;
  const { mayDo } = usePermissions();
  const canEditClients = mayDo(ACTIONS.USER_MANAGEMENT_EDIT);

  const [tab, setTab] = useState<TabId>("info");

  const [companyName, setCompanyName] = useState("");
  const [companyNameServerError, setCompanyNameServerError] = useState<
    string | null
  >(null);
  const [companyCode, setCompanyCode] = useState("");
  const [companyCodeServerError, setCompanyCodeServerError] = useState<
    string | null
  >(null);
  const [isActive, setIsActive] = useState(true);
  const [teamSlugs, setTeamSlugs] = useState<string[]>([]);
  const [isDemo, setIsDemo] = useState(false);
  // Teams a "Leave as is" answered, so their prompt stays closed.
  const [dismissedTeams, setDismissedTeams] = useState<string[]>([]);
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  // The owners the form opened with: the client's, or the creator on a new one.
  const [initialOwnerIds, setInitialOwnerIds] = useState<number[]>([]);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [addUserSearch, setAddUserSearch] = useState("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveDialogDate, setArchiveDialogDate] = useState<Date | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [saveConfirmSections, setSaveConfirmSections] = useState<
    ConfirmAccessChangesSection[]
  >([]);
  const [pendingAddedUsers, setPendingAddedUsers] = useState<PlatformUser[]>(
    [],
  );

  const {
    options: ownerOptions,
    loading: ownersLoading,
    onListboxScroll: ownersListboxOnScroll,
  } = useAdminUsersAutocompleteInfinite({
    skip: !open || tab !== "ownership",
    page_size: 50,
    search: ownerSearch,
    sort_by: "name",
    sort_order: "asc",
    user_type: "internal",
    is_archived: false,
  });

  const {
    options: allClientUserOptions,
    loading: addUserLoading,
    onListboxScroll: addUserListboxOnScroll,
  } = useAdminUsersAutocompleteInfinite({
    skip: !open || tab !== "users",
    page_size: 50,
    search: addUserSearch,
    sort_by: "name",
    sort_order: "asc",
    user_type: "client",
    is_archived: false,
  });

  const { data: clientUsersData, isLoading: isLoadingClientUsers } =
    useGetAdminUsersQuery(
      { client_id: client?.id, user_type: "client", page_size: 200 },
      { skip: tab !== "users" || isCreate || !client?.id },
    );
  const clientUsers = clientUsersData?.results ?? [];

  const assignedUserIds = useMemo(
    () =>
      new Set([
        ...clientUsers.map((u) => u.id),
        ...pendingAddedUsers.map((u) => u.id),
      ]),
    [clientUsers, pendingAddedUsers],
  );

  const addUserOptions = useMemo(
    () => allClientUserOptions.filter((u) => !assignedUserIds.has(u.id)),
    [allClientUserOptions, assignedUserIds],
  );

  // Not derived from ownerOptions: MUI wipes the input when value changes identity.
  const ownersByIdRef = useRef<Map<number, OwnerRef>>(new Map());
  useMemo(() => {
    for (const u of ownerOptions) ownersByIdRef.current.set(u.id, u);
  }, [ownerOptions]);
  useMemo(() => {
    for (const o of client?.calibrate_owners ?? [])
      ownersByIdRef.current.set(o.id, o);
  }, [client]);

  const ownerLabel = (id: number): OwnerRef =>
    ownersByIdRef.current.get(id) ?? {
      id,
      username: String(id),
      first_name: "",
      last_name: "",
    };
  const ownersLabelKey = ownerIds
    .map((id) => formatPlatformUserOptionLabel(ownerLabel(id)))
    .join("|");
  const selectedOwners: OwnerRef[] = useMemo(
    () => ownerIds.map((id) => ownerLabel(id)),
    [ownerIds, ownersLabelKey],
  );
  const ownerAcOptions: OwnerRef[] = useMemo(() => {
    const loaded = new Set(ownerOptions.map((u) => u.id));
    const missing = selectedOwners.filter((o) => !loaded.has(o.id));
    return missing.length ? [...missing, ...ownerOptions] : ownerOptions;
  }, [ownerOptions, selectedOwners]);

  useEffect(() => {
    if (!open) return;
    setTab("info");
    setArchiveDialogOpen(false);
    setArchiveDialogDate(null);
    setOwnerSearch("");
    setAddUserSearch("");
    setPendingAddedUsers([]);
    setDismissedTeams([]);
    if (client) {
      const owners = client.calibrate_owners.map((o) => o.id);
      setCompanyName(client.company_name);
      setCompanyCode(client.company_code);
      setIsActive(client.is_active);
      setTeamSlugs(client.service_teams.map((team) => team.slug));
      setIsDemo(client.is_demo);
      setOwnerIds(owners);
      setInitialOwnerIds(owners);
    } else {
      const creator = creatorRef.current;
      if (creator) ownersByIdRef.current.set(creator.id, creator);
      const owners = creator ? [creator.id] : [];
      setCompanyName("");
      setCompanyCode(generateCode());
      setIsActive(true);
      setTeamSlugs([]);
      setIsDemo(false);
      setOwnerIds(owners);
      setInitialOwnerIds(owners);
    }
  }, [open, client]);

  const { data: teamsAccess } = useGetServiceTeamsQuery(undefined, {
    skip: !open,
  });
  const teams = teamsAccess?.teams ?? [];
  // A client someone creates starts with what their group needs to keep seeing
  // it: their team, them among its owners, the demo mark on or off.
  const lockedTeam = (slug: string) =>
    isCreate && teams.some((t) => t.slug === slug && t.on_new_client);
  const effectiveTeamSlugs = isCreate
    ? Array.from(
        new Set([
          ...teamSlugs,
          ...teams.filter((t) => t.on_new_client).map((t) => t.slug),
        ]),
      )
    : teamSlugs;
  const demoForced = isCreate
    ? (teamsAccess?.demo_on_new_client ?? null)
    : null;
  const effectiveDemo = demoForced ?? isDemo;
  const canSetDemo = Boolean(teamsAccess?.can_set_demo) && demoForced === null;
  const creatorLocked = (id: number) =>
    isCreate &&
    Boolean(teamsAccess?.owner_on_new_client) &&
    id === creatorRef.current?.id;
  const keepLockedOwners = (ids: number[]) => {
    const creator = creatorRef.current;
    return creator && creatorLocked(creator.id) && !ids.includes(creator.id)
      ? [creator.id, ...ids]
      : ids;
  };
  const toggleTeam = (slug: string) =>
    setTeamSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  const addTeam = (slug: string) =>
    setTeamSlugs((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
  const teamName = (slug: string) =>
    teams.find((team) => team.slug === slug)?.name ??
    client?.service_teams.find((team) => team.slug === slug)?.name ??
    slug;
  const baselineTeams = (client?.service_teams ?? []).map((t) => t.slug).sort();
  const teamsChanged =
    JSON.stringify(baselineTeams) !== JSON.stringify([...teamSlugs].sort());
  const demoChanged = Boolean(client) && isDemo !== client?.is_demo;
  const ownersChanged = !sameIds(ownerIds, initialOwnerIds);
  const marks: ClientMarks = {
    teamSlugs: effectiveTeamSlugs,
    isDemo: effectiveDemo,
  };

  const handleArchiveNow = async () => {
    if (!client) return;
    try {
      const saved = await archiveClient({ id: client.id }).unwrap();
      showToast("Client archived.", "success");
      onSaved(saved);
      setArchiveDialogOpen(false);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to archive client.", "error");
    }
  };

  const handleScheduleArchive = async () => {
    if (!client || !archiveDialogDate) return;
    const d = archiveDialogDate;
    const dateStr = [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");
    try {
      const saved = await archiveClient({
        id: client.id,
        archiveDate: dateStr,
      }).unwrap();
      showToast(`Archive scheduled for ${dateStr}.`, "success");
      onSaved(saved);
      setArchiveDialogOpen(false);
      setArchiveDialogDate(null);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to schedule archive.", "error");
    }
  };

  const handleCancelSchedule = async () => {
    if (!client) return;
    try {
      const saved = await archiveClient({
        id: client.id,
        archiveDate: null,
      }).unwrap();
      showToast("Archive schedule cancelled.", "success");
      onSaved(saved);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to cancel schedule.", "error");
    }
  };

  const handleUnarchive = async () => {
    if (!client) return;
    try {
      const saved = await archiveClient({ id: client.id }).unwrap();
      showToast("Client un-archived.", "success");
      onSaved(saved);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to un-archive client.", "error");
    }
  };

  const handleAddUser = (_: unknown, selectedUser: PlatformUser | null) => {
    if (!selectedUser || !client) return;
    setPendingAddedUsers((prev) =>
      prev.some((u) => u.id === selectedUser.id)
        ? prev
        : [...prev, selectedUser],
    );
    setAddUserSearch("");
  };

  const handleRemovePendingUser = (userId: number) => {
    setPendingAddedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const hasUnsavedChanges = useMemo(() => {
    if (isCreate) {
      return (
        companyName.trim() !== "" ||
        !isActive ||
        teamSlugs.length > 0 ||
        isDemo ||
        ownersChanged ||
        pendingAddedUsers.length > 0
      );
    }
    if (!client) return false;
    return (
      companyName.trim() !== (client.company_name ?? "").trim() ||
      companyCode.trim() !== (client.company_code ?? "").trim() ||
      isActive !== client.is_active ||
      teamsChanged ||
      demoChanged ||
      ownersChanged ||
      pendingAddedUsers.length > 0
    );
  }, [
    isCreate,
    client,
    companyName,
    companyCode,
    isActive,
    teamSlugs,
    teamsChanged,
    isDemo,
    demoChanged,
    ownersChanged,
    pendingAddedUsers,
  ]);

  const unsavedSummary = useMemo(() => {
    if (isCreate) {
      const sections: string[] = [];
      if (companyName.trim() !== "") sections.push("Company name");
      if (!isActive) sections.push("Status (inactive)");
      if (isDemo) sections.push("Demo client");
      if (teamSlugs.length > 0) sections.push("Service teams");
      if (ownersChanged) sections.push("Owners");
      if (pendingAddedUsers.length > 0) sections.push("Users");
      return sections;
    }
    if (!client) return [];
    const sections: string[] = [];
    if (companyName.trim() !== (client.company_name ?? "").trim())
      sections.push("Company name");
    if (companyCode.trim() !== (client.company_code ?? "").trim())
      sections.push("Company code");
    if (isActive !== client.is_active) sections.push("Status");
    if (demoChanged) sections.push("Demo client");
    if (teamsChanged) sections.push("Service teams");
    if (ownersChanged) sections.push("Owners");
    if (pendingAddedUsers.length > 0) sections.push("Users");
    return sections;
  }, [
    isCreate,
    client,
    companyName,
    companyCode,
    isActive,
    isDemo,
    demoChanged,
    teamSlugs,
    teamsChanged,
    ownersChanged,
    pendingAddedUsers,
  ]);

  useLayoutEffect(() => {
    onUnsavedChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChange]);

  useLayoutEffect(() => {
    onUnsavedSummaryChange?.(unsavedSummary);
  }, [unsavedSummary, onUnsavedSummaryChange]);

  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  const [archiveClient, { isLoading: isArchiving }] =
    useArchiveClientMutation();
  const [updateAdminUser, { isLoading: isUpdatingUser }] =
    useUpdateAdminUserMutation();
  const [checkClientCodeAvailability] =
    useLazyCheckClientCodeAvailabilityQuery();
  const [checkClientNameAvailability] =
    useLazyCheckClientNameAvailabilityQuery();
  const isSaving = isCreating || isUpdating || isArchiving || isUpdatingUser;

  const showToast = (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));

  const companyNameClientError =
    companyName.trim().length > 50
      ? "Company name cannot exceed 50 characters."
      : undefined;
  const companyNameError =
    companyNameClientError ?? companyNameServerError ?? undefined;
  const companyCodeInvalidChars = /[0O1LlIi]/.test(companyCode)
    ? "0, O, I, 1 and L are not allowed in company codes."
    : undefined;
  const companyCodeClientError =
    companyCode.trim().length === 0
      ? "Company Code is required."
      : companyCodeInvalidChars;
  const companyCodeError =
    companyCodeClientError ?? companyCodeServerError ?? undefined;

  const canSave =
    companyName.trim().length > 0 &&
    companyName.trim().length <= 50 &&
    !companyNameServerError &&
    companyCode.trim().length > 0 &&
    !companyCodeInvalidChars &&
    !companyCodeServerError;

  const performSave = async () => {
    if (!canSave) return;
    try {
      let saved: Client;
      if (isCreate) {
        saved = await createClient({
          company_name: companyName.trim(),
          company_code: companyCode.trim().toUpperCase(),
          calibrate_owner_ids: ownerIds,
          service_teams: effectiveTeamSlugs,
          is_demo: effectiveDemo,
        }).unwrap();
        showToast("Client created successfully.", "success");
      } else {
        saved = await updateClient({
          id: client!.id,
          company_name: companyName.trim(),
          company_code: companyCode.trim().toUpperCase(),
          calibrate_owner_ids: ownerIds,
          service_teams: teamSlugs,
          is_demo: isDemo,
          is_active: isActive,
        }).unwrap();
        showToast("Client updated successfully.", "success");
      }
      if (pendingAddedUsers.length > 0) {
        for (const pu of pendingAddedUsers) {
          try {
            await updateAdminUser({
              id: pu.id,
              client_id: saved.id,
            }).unwrap();
          } catch (err: any) {
            showToast(
              err?.data?.error || `Failed to assign user ${pu.username}.`,
              "error",
            );
          }
        }
      }
      onSaved(saved);
    } catch (err: any) {
      const detail =
        err?.data?.errors?.company_code?.[0] ||
        err?.data?.errors?.company_name?.[0] ||
        err?.data?.error ||
        "Failed to save client.";
      showToast(detail, "error");
    }
  };

  const buildSaveConfirmSections = (): ConfirmAccessChangesSection[] => {
    const sections: ConfirmAccessChangesSection[] = [];
    if (isCreate) {
      const added: string[] = [];
      if (companyName.trim()) added.push("Company name");
      if (companyCode.trim()) added.push("Company code");
      if (!isActive) added.push("Status (inactive)");
      if (added.length > 0) {
        sections.push({ label: "INFO", added });
      }
      if (effectiveDemo)
        sections.push({ label: "DEMO", added: ["Demo client"] });
      if (effectiveTeamSlugs.length > 0) {
        sections.push({
          label: "SERVICE TEAMS",
          added: effectiveTeamSlugs.map(teamName),
        });
      }
      if (ownerIds.length > 0) {
        sections.push({
          label: "OWNERS",
          added: [`${ownerIds.length} owner(s)`],
        });
      }
      if (pendingAddedUsers.length > 0) {
        sections.push({
          label: "USERS",
          added: pendingAddedUsers.map((u) => formatPlatformUserOptionLabel(u)),
        });
      }
      return sections;
    }
    if (!client) return sections;
    const updated: string[] = [];
    if (companyName.trim() !== (client.company_name ?? "").trim())
      updated.push("Company name");
    if (isActive !== client.is_active) updated.push("Status");
    if (updated.length > 0) {
      sections.push({ label: "INFO", updated });
    }
    if (demoChanged) {
      sections.push(
        isDemo
          ? { label: "DEMO", added: ["Demo client"] }
          : { label: "DEMO", removed: ["Demo client"] },
      );
    }
    if (teamsChanged) {
      const teamSection: ConfirmAccessChangesSection = {
        label: "SERVICE TEAMS",
      };
      const added = teamSlugs.filter((slug) => !baselineTeams.includes(slug));
      const removed = baselineTeams.filter((slug) => !teamSlugs.includes(slug));
      if (added.length > 0) teamSection.added = added.map(teamName);
      if (removed.length > 0) teamSection.removed = removed.map(teamName);
      sections.push(teamSection);
    }
    const baselineOwnerIds = client.calibrate_owners.map((o) => o.id).sort();
    const currentOwnerIds = [...ownerIds].sort();
    if (JSON.stringify(baselineOwnerIds) !== JSON.stringify(currentOwnerIds)) {
      const addedOwners = ownerIds.filter(
        (id) => !baselineOwnerIds.includes(id),
      );
      const removedOwners = baselineOwnerIds.filter(
        (id) => !ownerIds.includes(id),
      );
      const ownerSection: ConfirmAccessChangesSection = { label: "OWNERS" };
      if (addedOwners.length > 0) {
        ownerSection.added = addedOwners.map((id) =>
          formatPlatformUserOptionLabel(ownerLabel(id)),
        );
      }
      if (removedOwners.length > 0) {
        ownerSection.removed = removedOwners.map((id) => {
          const o = client.calibrate_owners.find((u) => u.id === id);
          if (!o) return `ID ${id}`;
          const name = [o.first_name, o.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
          return name ? `${name} @${o.username}` : `@${o.username}`;
        });
      }
      sections.push(ownerSection);
    }
    if (pendingAddedUsers.length > 0) {
      sections.push({
        label: "USERS",
        added: pendingAddedUsers.map((u) => formatPlatformUserOptionLabel(u)),
      });
    }
    return sections;
  };

  const handleSave = () => {
    if (!canSave) return;
    const sections = buildSaveConfirmSections();
    if (sections.length > 0) {
      setSaveConfirmSections(sections);
      setSaveConfirmOpen(true);
      return;
    }
    performSave();
  };

  const clientId = client ? client.id : undefined;

  // Owners added in this form whose team does not work the client yet, by team.
  const teamPrompts: { slug: string; name: string; names: string[] }[] = [];
  for (const owner of selectedOwners) {
    if (initialOwnerIds.includes(owner.id) && !isCreate) continue;
    const rule = owner.access_group ? ruleOfOwner(owner.access_group) : null;
    if (!rule?.teamSlug || !teamMissing(rule, marks)) continue;
    if (dismissedTeams.includes(rule.teamSlug)) continue;
    const label = ownerName(owner) || owner.username;
    const prompt = teamPrompts.find((p) => p.slug === rule.teamSlug);
    if (prompt) prompt.names.push(label);
    else
      teamPrompts.push({
        slug: rule.teamSlug,
        name: rule.teamName ?? rule.teamSlug,
        names: [label],
      });
  }

  // Creating walks Profile then Ownership; Users needs the saved client.
  const archived = Boolean(client?.is_archived);
  const steps: FormStep<TabId>[] = [
    { key: "info", label: "Profile" },
    { key: "ownership", label: "Ownership", disabled: !isCreate && archived },
    ...(isCreate
      ? []
      : [{ key: "users" as const, label: "Users", disabled: archived }]),
  ];
  const isFirstStep = nextOpenStep(steps, tab, -1) === null;
  const isLastStep = nextOpenStep(steps, tab, 1) === null;
  const goToStep = (direction: 1 | -1) => {
    const next = nextOpenStep(steps, tab, direction);
    if (next) setTab(next);
  };

  useEffect(() => {
    const value = companyName.trim();
    if (!value) {
      setCompanyNameServerError(null);
      return;
    }
    if (client && value === client.company_name.trim()) {
      setCompanyNameServerError(null);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const res = await checkClientNameAvailability({
          value,
          excludeId: clientId,
        }).unwrap();
        if (cancelled) return;
        setCompanyNameServerError(
          res.available
            ? null
            : (res.message ?? "This company name is already in use."),
        );
      } catch {
        if (!cancelled) setCompanyNameServerError(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [companyName, client, clientId, checkClientNameAvailability]);

  useEffect(() => {
    const value = companyCode.trim();
    if (!value || !isCreate) {
      setCompanyCodeServerError(null);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const res = await checkClientCodeAvailability({
          value,
          excludeId: clientId,
        }).unwrap();
        if (cancelled) return;
        setCompanyCodeServerError(
          res.available
            ? null
            : (res.message ?? "This company code is already in use."),
        );
      } catch {
        if (!cancelled) setCompanyCodeServerError(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [companyCode, isCreate, clientId, checkClientCodeAvailability]);

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          height: { xs: "90vh", sm: 840 },
          maxHeight: { xs: "90vh", sm: 840 },
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <FormHeader
          avatar={
            isCreate ? (
              <BusinessOutlined fontSize="small" />
            ) : (
              client!.company_name[0]?.toUpperCase()
            )
          }
          title={isCreate ? "New Client" : client!.company_name}
          adornment={
            client?.is_archived ? (
              <StatusChip status="archived" />
            ) : (
              client?.scheduled_archive_date && (
                <StatusChip status="scheduled" />
              )
            )
          }
          subtitle={client?.company_code}
          onClose={onClose}
          closeDisabled={isSaving}
        />

        <Box
          sx={{
            minWidth: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <StepTabs<TabId> steps={steps} value={tab} onChange={setTab} />
        </Box>

        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
            {tab === "info" && (
              <Box>
                <StepHeading
                  title="Client Profile"
                  subtitle="Set the company name. The client code is issued once, when the client is created."
                />
                <FieldLabel required htmlFor="client-form-name">
                  Company Name
                </FieldLabel>
                <TextField
                  id="client-form-name"
                  placeholder="Company Name"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setCompanyNameServerError(null);
                  }}
                  fullWidth
                  required
                  autoFocus
                  size="small"
                  disabled={isSaving}
                  error={Boolean(companyNameError)}
                  helperText={companyNameError}
                  sx={{ mb: 2 }}
                />
                <FieldLabel required htmlFor="client-form-code">
                  Company Code
                </FieldLabel>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <TextField
                    id="client-form-code"
                    value={companyCode}
                    size="small"
                    required
                    disabled={isSaving}
                    InputProps={{ readOnly: true }}
                    inputProps={{
                      maxLength: 10,
                      style: { fontFamily: "monospace", fontWeight: 700 },
                    }}
                    sx={{ flex: 1 }}
                    error={Boolean(companyCodeError)}
                    helperText={
                      companyCodeError ??
                      (isCreate
                        ? "Generated for you. Use refresh to get a new code."
                        : "Issued once at creation. It cannot be changed.")
                    }
                  />
                  {isCreate && (
                    <Tooltip title="Regenerate code">
                      <span>
                        <IconButton
                          onClick={() => setCompanyCode(generateCode())}
                          disabled={isSaving}
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box
                  sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      Demo Client
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ lineHeight: 1.4 }}
                    >
                      Toggle this switch to set this client up with sample data
                      used for Sales Demo purposes.
                    </Typography>
                  </Box>
                  <Switch
                    checked={effectiveDemo}
                    disabled={isSaving || !canEditClients || !canSetDemo}
                    onChange={(e) => setIsDemo(e.target.checked)}
                    inputProps={{ "aria-label": "Demo Client" }}
                  />
                </Box>
                {canEditClients && demoForced !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {demoForced
                      ? "Your access group sees demo clients only, so a client " +
                        "you create is a demo client."
                      : "Your access group leaves demo clients out, so a client " +
                        "you create is not a demo client."}
                  </Typography>
                )}
                {canEditClients &&
                  demoForced === null &&
                  teamsAccess &&
                  !teamsAccess.can_set_demo && (
                    <Typography variant="caption" color="text.secondary">
                      Only an access group that sees every client marks demo
                      clients.
                    </Typography>
                  )}
              </Box>
            )}

            {tab === "ownership" && (
              <>
                <StepHeading
                  title="Service Teams"
                  subtitle={
                    isCreate
                      ? "Select which Calibrate teams work with this client."
                      : "Which Calibrate teams work this client. Sets the " +
                        "default client list for each team's access groups."
                  }
                />
                <Box sx={{ mb: 3 }}>
                  {teams.map((team, index) => {
                    const locked = lockedTeam(team.slug);
                    const since = client?.service_teams.find(
                      (t) => t.slug === team.slug,
                    )?.since;
                    return (
                      <Box
                        key={team.slug}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          borderTop: index > 0 ? "1px solid" : "none",
                          borderColor: "divider",
                        }}
                      >
                        <FormControlLabel
                          disabled={
                            isSaving ||
                            !canEditClients ||
                            locked ||
                            !team.editable
                          }
                          control={
                            <Checkbox
                              size="small"
                              checked={locked || teamSlugs.includes(team.slug)}
                              onChange={() => toggleTeam(team.slug)}
                            />
                          }
                          label={team.name}
                        />
                        {since && teamSlugs.includes(team.slug) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace", flexShrink: 0 }}
                          >
                            since {formatSince(since)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  {isCreate && teams.some((team) => team.on_new_client) && (
                    <Typography variant="caption" color="text.secondary">
                      A client you create carries your access group&apos;s team,
                      so it stays within your access.
                    </Typography>
                  )}
                  {!isCreate &&
                    canEditClients &&
                    teams.some((team) => !team.editable) && (
                      <Typography variant="caption" color="text.secondary">
                        You can change only the teams your access group covers.
                      </Typography>
                    )}
                </Box>
                <StepHeading
                  title="Client Ownership"
                  subtitle={
                    isCreate
                      ? "Assign the Calibrate internal users who own this " +
                        "client account."
                      : "Assign the Calibrate internal users who own this " +
                        "client account. Each person's team is shown - that " +
                        "comes from their access group."
                  }
                />
                <Box display="flex" flexDirection="column" gap={2}>
                  <Autocomplete
                    multiple
                    options={ownerAcOptions}
                    loading={ownersLoading}
                    componentsProps={platformUserAutocompleteComponentsProps}
                    ListboxComponent={PlatformUserAutocompleteListbox}
                    ListboxProps={{ onScroll: ownersListboxOnScroll }}
                    value={selectedOwners}
                    onChange={(_, newValue) =>
                      setOwnerIds(keepLockedOwners(newValue.map((v) => v.id)))
                    }
                    getOptionLabel={formatPlatformUserOptionLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    disabled={isSaving || !canEditClients}
                    renderOption={renderPlatformUserAutocompleteOption}
                    filterOptions={(x) => x}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") setOwnerSearch(value);
                      else if (reason === "clear") setOwnerSearch("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Calibrate Owners"
                        size="small"
                        placeholder="Search internal users..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { onDelete, ...tagProps } = getTagProps({
                          index,
                        });
                        return (
                          <Chip
                            {...tagProps}
                            onDelete={
                              creatorLocked(option.id) ? undefined : onDelete
                            }
                            key={option.id}
                            label={ownerName(option) || option.username}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        );
                      })
                    }
                  />
                  {teamPrompts.map((prompt) => {
                    const team = teams.find((t) => t.slug === prompt.slug);
                    const canAdd = canEditClients && Boolean(team?.editable);
                    return (
                      <Alert key={prompt.slug} severity="warning" icon={false}>
                        <Typography variant="body2">
                          <strong>{joinNames(prompt.names)}</strong>{" "}
                          {prompt.names.length > 1 ? "are" : "is"} on{" "}
                          {prompt.name}, which isn&apos;t a service team on this
                          client.{" "}
                          {canAdd
                            ? "Add it?"
                            : `Only an access group that sees every client, ` +
                              `or every ${prompt.name} client, can add it.`}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          {canAdd && (
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              disabled={isSaving}
                              onClick={() => addTeam(prompt.slug)}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Add {prompt.name}
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() =>
                              setDismissedTeams((prev) => [
                                ...prev,
                                prompt.slug,
                              ])
                            }
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Leave as is
                          </Button>
                        </Stack>
                      </Alert>
                    );
                  })}
                  {selectedOwners.length > 0 && (
                    <Box>
                      <Divider sx={{ mb: 1 }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        CURRENT OWNERS
                      </Typography>
                      <Table size="small" sx={{ mt: 0.5 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                              Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                              Username
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                              Team
                            </TableCell>
                            <TableCell sx={{ width: 40 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOwners.map((o) => {
                            const rule = o.access_group
                              ? ruleOfOwner(o.access_group)
                              : null;
                            const hidden = hiddenReason(rule, marks);
                            return (
                              <TableRow key={o.id}>
                                <TableCell>
                                  {ownerName(o) || "\u2014"}
                                  {rule && hidden && (
                                    <Typography
                                      variant="caption"
                                      color="warning.dark"
                                      display="block"
                                    >
                                      {hidden === "team"
                                        ? `Until it is added, ${
                                            o.first_name || o.username
                                          } does not see this client.`
                                        : `Does not see this client: ${lowerFirst(
                                            hiddenText(hidden, rule),
                                          )}`}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    @{o.username}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {rule?.teamName ? (
                                    teamMissing(rule, marks) ? (
                                      <Tooltip
                                        title={`${rule.teamName} isn't a service team on this client.`}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            color: "warning.dark",
                                            fontWeight: 600,
                                            fontSize: 13,
                                          }}
                                        >
                                          {rule.teamName}
                                          <WarningAmber
                                            sx={{ fontSize: 15 }}
                                            aria-label="Not a service team on this client"
                                          />
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      <Typography variant="body2">
                                        {rule.teamName}
                                      </Typography>
                                    )
                                  ) : (
                                    "\u2014"
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove ${o.username}`}
                                    disabled={
                                      isSaving ||
                                      !canEditClients ||
                                      creatorLocked(o.id)
                                    }
                                    onClick={() =>
                                      setOwnerIds((prev) =>
                                        prev.filter((id) => id !== o.id),
                                      )
                                    }
                                    sx={{ p: 0.25 }}
                                  >
                                    <Close fontSize="inherit" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {tab === "users" && !isCreate && (
              <>
                <StepHeading
                  title="Client Users"
                  subtitle="Assign client users to this client. A user moved here from another client loses their folder and integration access."
                />
                <Box display="flex" flexDirection="column" gap={2}>
                  <Autocomplete
                    options={addUserOptions}
                    loading={addUserLoading}
                    componentsProps={platformUserAutocompleteComponentsProps}
                    ListboxComponent={PlatformUserAutocompleteListbox}
                    ListboxProps={{ onScroll: addUserListboxOnScroll }}
                    value={null}
                    onChange={handleAddUser}
                    getOptionLabel={formatPlatformUserOptionLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    getOptionDisabled={(option) =>
                      Boolean(option.client_is_archived)
                    }
                    disabled={isSaving || !canEditClients}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box
                          sx={{
                            width: "100%",
                            py: 0.4,
                            opacity: option.client_is_archived ? 0.45 : 1,
                          }}
                        >
                          <PlatformUserLabelBlock user={option} />
                          {option.client_is_archived ? (
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ display: "block", mt: 0.25 }}
                            >
                              Archived
                            </Typography>
                          ) : option.client_name ? (
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ display: "block", mt: 0.25 }}
                            >
                              Current Client: {option.client_name}
                            </Typography>
                          ) : null}
                        </Box>
                      </li>
                    )}
                    filterOptions={(x) => x}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") setAddUserSearch(value);
                      else if (reason === "clear") setAddUserSearch("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add Client User"
                        size="small"
                        placeholder="Search users to assign..."
                      />
                    )}
                  />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    ASSIGNED CLIENT USERS (
                    {isLoadingClientUsers
                      ? "\u2026"
                      : clientUsers.length + pendingAddedUsers.length}
                    )
                  </Typography>
                  {isLoadingClientUsers ? (
                    <Box>
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="text"
                          height={40}
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  ) : clientUsers.length === 0 &&
                    pendingAddedUsers.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{ py: 2 }}
                    >
                      No users assigned to this client yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Username
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Roles
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: 12, width: 40 }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientUsers.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell>
                              {u.first_name
                                ? `${u.first_name} ${u.last_name ?? ""}`.trim()
                                : "\u2014"}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                @{u.username}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {u.profile.is_data_storage_user && (
                                  <Chip
                                    label="Data Storage"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: 10,
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                                {u.profile.is_integration_dashboard_user && (
                                  <Chip
                                    label={
                                      u.profile.integrations_experience ===
                                      "platform"
                                        ? "Integration Platform"
                                        : "Integration"
                                    }
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      ...(u.profile.integrations_experience ===
                                      "platform"
                                        ? {
                                            bgcolor: "#f1f8e9",
                                            color: "#33691e",
                                          }
                                        : {
                                            bgcolor: "#fff3e0",
                                            color: "#e65100",
                                          }),
                                    }}
                                  />
                                )}
                                {u.profile.is_ai_agent_user && (
                                  <Chip
                                    label="AI Agent"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      bgcolor: "rgba(46,125,50,0.10)",
                                      color: "#2e7d32",
                                    }}
                                  />
                                )}
                                {u.profile.is_secure_file_upload_user && (
                                  <Chip
                                    label={
                                      <SecureExchangeRoleLabel
                                        confidential={Boolean(
                                          u.profile.can_access_confidential,
                                        )}
                                        iconSize={11}
                                      />
                                    }
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      bgcolor: "#fbe9e7",
                                      color: "#bf360c",
                                    }}
                                  />
                                )}
                                {!u.profile.is_data_storage_user &&
                                  !u.profile.is_integration_dashboard_user &&
                                  !u.profile.is_ai_agent_user &&
                                  !u.profile.is_secure_file_upload_user && (
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                    >
                                      No roles
                                    </Typography>
                                  )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {pendingAddedUsers.map((u) => (
                          <TableRow key={`pending-${u.id}`}>
                            <TableCell>
                              {u.first_name || u.last_name
                                ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
                                : "\u2014"}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                @{u.username}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label="Pending"
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  bgcolor: "#e3f2fd",
                                  color: "#1565c0",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePendingUser(u.id)}
                                sx={{
                                  color: "error.main",
                                  p: 0.25,
                                  "&:hover": {
                                    bgcolor: "rgba(211,47,47,0.08)",
                                  },
                                }}
                              >
                                <Close fontSize="inherit" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </>
            )}
          </Box>
        </CustomScrollbars>

        {isCreate ? (
          <FormFooter
            mode="create"
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            onBack={() => goToStep(-1)}
            onCancel={onClose}
            onContinue={() => goToStep(1)}
            continueDisabled={tab === "info" && !canSave}
            onCreate={handleSave}
            createDisabled={
              isSaving || !canSave || !canEditClients || !hasUnsavedChanges
            }
            createLabel="Create Client"
            creating={isSaving}
          />
        ) : (
          <FormFooter
            mode="edit"
            isFirstStep={isFirstStep}
            onBack={() => goToStep(-1)}
            onCancel={onClose}
            unsaved={hasUnsavedChanges}
          >
            {client && (
              <Button
                variant="outlined"
                size="small"
                color={client.is_archived ? "success" : "error"}
                onClick={
                  client.is_archived
                    ? handleUnarchive
                    : () => setArchiveDialogOpen(true)
                }
                disabled={isSaving || !canEditClients}
                sx={FOOTER_BUTTON_SX}
              >
                {client.is_archived ? "Un-Archive" : "Archive"}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={
                isSaving || !canSave || !canEditClients || !hasUnsavedChanges
              }
              startIcon={
                isSaving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
              sx={{ ...FOOTER_BUTTON_SX, fontWeight: 700 }}
            >
              Save Changes
            </Button>
          </FormFooter>
        )}
      </DialogContent>

      {/* Archive dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Archive Client
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleArchiveNow}
            disabled={isArchiving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isArchiving ? "Processing..." : "Archive Immediately"}
          </Button>

          <Divider>
            <Typography variant="caption" color="text.secondary">
              or schedule
            </Typography>
          </Divider>

          <Box>
            <Typography variant="body2" fontWeight={700} mb={1}>
              Schedule archive date
            </Typography>
            {client?.scheduled_archive_date && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Currently scheduled for{" "}
                {(() => {
                  const d = new Date(
                    client.scheduled_archive_date + "T00:00:00",
                  );
                  return d.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  });
                })()}
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Archive on date"
                value={archiveDialogDate}
                onChange={(v) => setArchiveDialogDate(v ?? null)}
                minDate={new Date(Date.now() + 86400000)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { "& .MuiOutlinedInput-root": { borderRadius: 1 } },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              disabled={!archiveDialogDate || isArchiving}
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1,
                fontSize: 12,
              }}
              onClick={handleScheduleArchive}
            >
              {isArchiving ? "Processing..." : "Set Schedule"}
            </Button>
          </Box>

          {client?.scheduled_archive_date && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Button
                variant="text"
                color="error"
                fullWidth
                size="small"
                disabled={isArchiving}
                sx={{ textTransform: "none", fontWeight: 600 }}
                onClick={handleCancelSchedule}
              >
                Cancel existing schedule
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setArchiveDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmChangesDialog
        open={saveConfirmOpen}
        title={isCreate ? "Confirm Create Client" : "Confirm Save Changes"}
        confirmLabel={isCreate ? "Create Client" : "Save Changes"}
        intro={
          pendingAddedUsers.some((u) => u.client_name) ? (
            <Typography
              variant="body2"
              sx={{
                color: "error.main",
                fontWeight: 700,
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "rgba(211,47,47,0.08)",
              }}
            >
              Users reassigned from another client will lose all folder and
              integration access.
            </Typography>
          ) : null
        }
        sections={saveConfirmSections.map((section) =>
          accessChangeSection(section, "warning"),
        )}
        onCancel={() => setSaveConfirmOpen(false)}
        onConfirm={() => {
          setSaveConfirmOpen(false);
          performSave();
        }}
      />
    </Dialog>
  );
}
